import { CircleDot, Layers, PanelRight } from "lucide-react";

import { SEGMENT_TOUR_TARGET } from "./constants";

import type { TourDefinition } from "@/features/tours/types";

export const OVERVIEW_TOUR_STEP_ID = {
  WHAT_IS: "what_is",
  STATUSES: "statuses",
  ADD: "add",
  WHERE_USED: "where_used",
  VACANCIES: "vacancies",
  ACTIVITY: "activity",
  OPEN: "open",
  ARCHIVE: "archive",
} as const;

type OverviewTourStepId = (typeof OVERVIEW_TOUR_STEP_ID)[keyof typeof OVERVIEW_TOUR_STEP_ID];

const OVERVIEW_SECTION = {
  LIST: "list",
  DETAILS: "details",
} as const;

export const SEGMENT_OVERVIEW_TOUR: TourDefinition<OverviewTourStepId> = {
  id: "segment_overview",
  copyKey: "admin.segments.overviewTour",
  steps: [
    { id: OVERVIEW_TOUR_STEP_ID.WHAT_IS, section: OVERVIEW_SECTION.LIST, target: SEGMENT_TOUR_TARGET.OVERVIEW_SEGMENT, placement: "right" },
    { id: OVERVIEW_TOUR_STEP_ID.STATUSES, section: OVERVIEW_SECTION.LIST, target: SEGMENT_TOUR_TARGET.OVERVIEW_STATUS, placement: "bottom-start" },
    { id: OVERVIEW_TOUR_STEP_ID.ADD, section: OVERVIEW_SECTION.LIST, target: SEGMENT_TOUR_TARGET.OVERVIEW_ADD, placement: "bottom-end" },
    { id: OVERVIEW_TOUR_STEP_ID.WHERE_USED, section: OVERVIEW_SECTION.DETAILS, target: SEGMENT_TOUR_TARGET.OVERVIEW_WHERE_USED, placement: "left" },
    { id: OVERVIEW_TOUR_STEP_ID.VACANCIES, section: OVERVIEW_SECTION.DETAILS, target: SEGMENT_TOUR_TARGET.OVERVIEW_VACANCIES, placement: "left" },
    { id: OVERVIEW_TOUR_STEP_ID.ACTIVITY, section: OVERVIEW_SECTION.DETAILS, target: SEGMENT_TOUR_TARGET.OVERVIEW_ACTIVITY, placement: "left" },
    { id: OVERVIEW_TOUR_STEP_ID.OPEN, section: OVERVIEW_SECTION.DETAILS, target: SEGMENT_TOUR_TARGET.OVERVIEW_OPEN, placement: "bottom-end" },
    { id: OVERVIEW_TOUR_STEP_ID.ARCHIVE, section: OVERVIEW_SECTION.DETAILS, target: SEGMENT_TOUR_TARGET.OVERVIEW_ARCHIVE, placement: "bottom-end" },
  ],
  sections: Object.values(OVERVIEW_SECTION),
  finishPlacement: "bottom-end",
  welcomeFeatures: [
    { key: "feature1", Icon: Layers },
    { key: "feature2", Icon: CircleDot },
    { key: "feature3", Icon: PanelRight },
  ],
};

export const DETAIL_STEP_IDS: ReadonlySet<string> = new Set(
  SEGMENT_OVERVIEW_TOUR.steps
    .filter(({ section }) => section === OVERVIEW_SECTION.DETAILS)
    .map(({ id }) => id),
);

type OverviewTourStepAvailability = {
  canCreate: boolean;
  canEdit: boolean;
};

export const getOverviewTourStepIds = ({ canCreate, canEdit }: OverviewTourStepAvailability): OverviewTourStepId[] =>
  SEGMENT_OVERVIEW_TOUR.steps
    .filter(({ id }) => {
      if (id === OVERVIEW_TOUR_STEP_ID.ADD) return canCreate;
      if (id === OVERVIEW_TOUR_STEP_ID.ARCHIVE) return canEdit;
      return true;
    })
    .map(({ id }) => id);

export const getOverviewTourSelection = (
  stepId: string | undefined,
  selectedId: string | undefined,
  firstSegmentId: string | undefined,
): string | undefined => (stepId && DETAIL_STEP_IDS.has(stepId) ? (selectedId ?? firstSegmentId) : undefined);
